import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Controller,
  FieldValues,
  FieldPath,
  UseControllerProps,
  useFormState,
} from 'react-hook-form';
import { useModal } from '@pcmp/react-providers';
import Button from '@ingka/button';
import PlusIcon from '@ingka/ssr-icon/paths/plus';
import { AsyncImage } from '@/components/helpers/image';
import { getter } from '@/lib/utils/object';
import { extractErrorMessage, ErrorLabel } from '../error-message';
import styles from './index.module.css';

type ErrorMessage = string | { i18nKey: string };
type FieldErrorLike = { message?: ErrorMessage; type?: string };
export type ImageEntryError = { value?: FieldErrorLike } | undefined;

export type ImagePickerProps = {
  className?: string;
  title?: string;
  images?: string[];
  disabled?: boolean;
  mimeTypes?: string[];
  errors?: ImageEntryError[];
  // Used to build a traceable object name for uploaded images (location/tag).
  locationSlug?: string;
  tag: string;
  onChange: (images: string[]) => Promise<void>;
};

export const ImagePicker: FC<ImagePickerProps> = ({
  className = '',
  title,
  images = [],
  disabled = false,
  mimeTypes,
  errors,
  locationSlug,
  tag,
  onChange,
}) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Only entries with an actual error produce a message; undefined/valid
  // entries must be skipped, otherwise they'd fall back to a bogus
  // "label.defaultError" message.
  const errorMsgs = errors
    ?.map((err) => err?.value && String(t(...extractErrorMessage(err.value))))
    .filter((msg): msg is string => !!msg);

  const updateScrollState = useCallback(() => {
    const node = scrollerRef.current;
    if (!node) return;

    setCanScrollLeft(node.scrollLeft > 0);
    setCanScrollRight(
      Math.ceil(node.scrollLeft + node.clientWidth) < node.scrollWidth
    );
  }, []);

  const handleAdd = useCallback(async () => {
    if (disabled) return;

    const component = await import('@/components/siders/image-picker').then(
      (m) => m.default
    );

    openModal({
      component,
      props: {
        title: title || t('label.addImage'),
        images,
        mimeTypes,
        locationSlug,
        tag,
        onChange,
      },
    });
  }, [
    disabled,
    title,
    images,
    mimeTypes,
    locationSlug,
    tag,
    onChange,
    openModal,
  ]);

  useEffect(() => {
    updateScrollState();

    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [images, updateScrollState]);

  const fadeClass =
    canScrollLeft && canScrollRight
      ? styles.fadeBoth
      : canScrollLeft
      ? styles.fadeLeft
      : canScrollRight
      ? styles.fadeRight
      : '';

  return (
    <div className={`${className} mb-5`}>
      {title && <p className="text-sm mb-2">{title}</p>}

      <div className={`${styles.container} flex items-center gap-x-3`}>
        {images.length > 0 ? (
          <div
            ref={scrollerRef}
            className={`${styles.scroller} ${fadeClass} flex gap-x-2 grow`}
            onScroll={updateScrollState}
          >
            {images.map((image, index) => (
              <div
                key={`${image}-${index}`}
                className={`${styles.thumbnail} shrink-0 bg-skapa-neutral-2 ${
                  errors?.[index]?.value ? styles.thumbnailError : ''
                }`}
              >
                <AsyncImage
                  src={image}
                  alt={`${title || t('label.addImage')} ${index + 1}`}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className={`${styles.empty} grow`}>
            <p className="text-sm text-skapa-text-3">{t('label.noImages')}</p>
          </div>
        )}

        <Button
          className="ml-2 shrink-0"
          title={t('label.addImage')}
          iconOnly
          ssrIcon={PlusIcon}
          type="primary"
          size="small"
          disabled={disabled}
          data-testid="addImageBtn"
          onClick={handleAdd}
        />
      </div>

      {errorMsgs &&
        errorMsgs.length > 0 &&
        errorMsgs.map((msg, index) => (
          <ErrorLabel key={index} className="mt-2 !mb-0" message={msg} />
        ))}
    </div>
  );
};

export default ImagePicker;

export type ControlledImagePickerProps<T extends FieldValues> = {
  className?: string;
  title?: string;
  name: FieldPath<T>;
  rules?: UseControllerProps<T>['rules'];
  forcedError?: FieldErrorLike;
  disabled?: boolean;
  mimeTypes?: string[];
  locationSlug?: string;
  tag: string;
};

/**
 * Wraps `ImagePicker` with `react-hook-form`'s `Controller`, mapping the
 * component's plain `string[]` API to the form's `{ value: string }[]` field
 * shape, and surfacing validation errors the same way other `Controlled*`
 * components do.
 *
 * The `imageUrls` schema validates each entry individually (`value: string().url()`),
 * so `errors.imageUrls` is normally a per-index array of entry errors (invalid
 * URLs). A root "required" rule (added via the `rules` prop) instead sets a
 * single error directly on that same array-like object; it's wrapped as a
 * single-entry array so its message still surfaces below the picker (this
 * only happens when there are no images, so there's no thumbnail to
 * mismatch it against).
 */
export function ControlledImagePicker<T extends FieldValues>(
  props: ControlledImagePickerProps<T>
) {
  const {
    className,
    title,
    name,
    rules,
    forcedError,
    disabled,
    mimeTypes,
    locationSlug,
    tag,
  } = props;

  const { errors } = useFormState({ name });
  const fieldError = (forcedError || getter(errors, name)) as
    | FieldErrorLike
    | ImageEntryError[]
    | undefined;
  const itemErrors = Array.isArray(fieldError)
    ? fieldError
    : fieldError?.message
    ? [{ value: fieldError }]
    : undefined;

  return (
    <Controller
      name={name}
      rules={rules}
      render={({ field }) => {
        const entries: { value: string }[] = field.value || [];

        return (
          <ImagePicker
            className={className}
            title={title}
            disabled={disabled}
            mimeTypes={mimeTypes}
            errors={itemErrors}
            locationSlug={locationSlug}
            tag={tag}
            images={entries.map(({ value }) => value)}
            onChange={async (images) => {
              field.onChange(images.map((value) => ({ value })));
            }}
          />
        );
      }}
    />
  );
}
